<template>
  <UkaskoLoader :is-loading="!isActivityTypesReady">
    <FormContainer class="register__organization-data-form__form-container">
      <div class="organization-data-form__text-block">
        <h2 class="organization-data-form__title">Вітаю в UConnect</h2>
        <p class="organization-data-form__description">Ми надійна CRM-платформа для агентів і партнерських мереж</p>
      </div>
      <StatusProgress
        class="organization-data-form__progress"
        :current-step="getCurrentStep"
      />
      <form class="organization-data__form" @submit.prevent="handleSubmit">
        <LabeledTextInput
          class="organization-data__input--name"
          label="Назва компанії"
          placeholder="Введіть назву компанії"
          v-model="form.organizationName"
          :error-messages="errors.organizationName ? [errors.organizationName] : []"
          @input="clearError('organizationName')"
        />
        <LabeledTextInput
          class="organization-data__input--last-name"
          label="Імʼя контактної особи"
          placeholder="Введіть імʼя контактної особи"
          v-model="form.contactPerson"
          :error-messages="errors.contactPerson ? [errors.contactPerson] : []"
          @input="clearError('contactPerson')"
        />
        <LabeledTextInput
          class="organization-data__input--patronymic"
          label="ЄДРПОУ"
          placeholder="Введіть ЄДРПОУ"
          mask="number"
          v-model="form.edrpou"
          :error-messages="errors.edrpou ? [errors.edrpou] : []"
          @input="clearError('edrpou')"
        />
        <LabeledSelectInput
          class="organization-data__input--activity"
          label="Вид діяльності"
          placeholder="Оберіть вид діяльності"
          :options="prepareActivities"
          icon-color="#6171E4"
          v-model="form.activity"
          :error-messages="errors.activity ? [errors.activity] : []"
        />
        <div class="organization-data__next-step">
          <OrangeFancyButton
            class="organization-data__next-step--button"
            type="submit"
            :disabled="!canSwitchToNextStep"
          >
            Далі
          </OrangeFancyButton>
          <a
            class="organization-data__prev-step--button"
            @click="switchPreviousStep"
          >
            Назад
          </a>
        </div>
        <div class="organization-data__login">
          <p class="organization-data__login-text">
            У вас вже є аккаунт?
            <SmartLink
              class="organization-data__link--login"
              :route="{ name: 'login' }"
            >
              Увійти
            </SmartLink>
          </p>
        </div>
      </form>
    </FormContainer>
  </UkaskoLoader>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import StatusProgress from '../status-progress';
import { FormContainer } from '@/shared/ui/account';
import { LabeledSelectInput, LabeledTextInput, OrangeFancyButton } from '@/shared/ui/form';
import { SmartLink } from '@/shared/ui/link';
import { UkaskoLoader } from '@/shared/ui/ukasko-loader';

export default {
  data() {
    return {
      form: {
        organizationName: '',
        contactPerson: '',
        edrpou: '',
        activity: '',
      },
      errors: {
        organizationName: '',
        contactPerson: '',
        edrpou: '',
        activity: '',
      },
    };
  },
  created() {
    this.form.organizationName = this.getAccountOrganizationName;
    this.form.contactPerson = this.getAccountContactPerson;
    this.form.edrpou = this.getAccountEDRPOU;
    this.form.activity = this.getAccountActivity;
  },
  components: {
    SmartLink, OrangeFancyButton, LabeledTextInput, LabeledSelectInput, StatusProgress, FormContainer, UkaskoLoader
  },
  computed: {
    ...mapGetters('registerModule', [
      'getCurrentStep', 'getAccountOrganizationName', 'getAccountContactPerson', 'getAccountEDRPOU', 'getAccountActivity',
    ]),
    ...mapGetters('activityTypeModule', ['isActivityTypesReady', 'getActivityTypes']),
    prepareActivities() {
      return this.getActivityTypes?.map(activityType => {
        return { value: activityType.id, label: activityType.name };
      });
    },
    canSwitchToNextStep() {
      return !!this.form.organizationName && !!this.form.contactPerson && !!this.form.edrpou && !!this.form.activity;
    },
  },
  methods: {
    ...mapActions('registerModule', ['switchCurrentStep', 'switchPreviousStep', 'setAccountOrganizationData']),
    clearError(field) {
      this.errors[field] = '';
    },
    validateForm() {
      this.errors.organizationName = !this.form.organizationName.trim() ? 'Назва компанії обов\'язкове до заповнення' : null;
      this.errors.contactPerson = !this.form.contactPerson.trim() ? 'Ім\'я контактної особи обов\'язкове до заповнення' : null;
      this.errors.edrpou = !this.form.edrpou.trim() ? 'Код ЄДРПОУ обов\'язкове до заповнення' : null;
      this.errors.activity = !this.form.activity ? 'Оберіть вид діяльності' : null;

      return !this.errors.organizationName || !this.errors.contactPerson || !this.errors.edrpou || !this.errors.activity;
    },
    async handleSubmit() {
      if (this.validateForm()) {
        this.setAccountOrganizationData({
          organizationName: this.form.organizationName,
          contactPerson: this.form.contactPerson,
          edrpou: this.form.edrpou,
          activity: this.form.activity,
        });
        this.switchCurrentStep();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.register__organization-data-form {
  &__form-container {
    margin: 170px auto 0;
    width: 600px;

    @media (max-width: 630px) {
      margin: 50px 16px 0;
      width: auto;
    }
  }
}

.organization-data-form {
  &__title {
    font-family: "IBM Plex Sans", sans-serif;
    font-size: 36px;
    font-weight: 400;
    color: #0C1421;
    text-align: center;
    margin-bottom: 16px;

    @media (max-width: 630px) {
      font-size: 24px;
    }
  }

  &__description {
    font-size: 18px;
    font-weight: 400;
    color: #313957;
    text-align: center;

    @media (max-width: 630px) {
      font-size: 14px;
    }
  }

  &__progress {
    margin-bottom: 16px;
  }
}

.organization-data {
  &__input {
    &--name, &--last-name, &--patronymic {
      margin-bottom: 16px;
    }

    &--activity {
      margin-bottom: 32px;
    }
  }

  &__next-step {
    margin-bottom: 32px;
  }

  &__prev-step {
    &--button {
      display: inline-block;
      font-family: 'Inter', sans-serif;
      font-size: 16px;
      font-weight: 400;
      color: #313957;
      padding: 16px 24px;
      width: 100%;
      text-align: center;
      cursor: pointer;
    }
  }

  &__login {
    text-align: center;
  }

  &__link {
    &--login {
      font-family: 'Inter', sans-serif;
      font-size: 16px;
      font-weight: 400;
      color: #6171E4;

      &:hover {
        text-decoration: underline;
      }
    }
  }
}

::v-deep .input-label,
.input-label {
  position: relative;
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 400;
  color: #494E53;
  margin-bottom: 8px;
}

::v-deep .input-text, .input-text, ::v-deep .select, .select {
  position: relative !important;
  font-family: 'Inter', sans-serif !important;
  font-size: 22px !important;
  font-weight: 400 !important;
  background-color: #EBF2FF !important;
  width: 515px !important;
  border-radius: 16px !important;
  border: none !important;
  color: #494E53 !important;
  padding: 16px 24px !important;

  @media (max-width: 1300px) {
    width: 100% !important;
  }

  &::placeholder {
    color: #8E9DB7 !important;
  }

  &--has-error {
    border: 2px solid #FF3B30 !important;
  }
}

::v-deep .select__value {
  font-family: 'Inter', sans-serif !important;
  font-size: 22px !important;
  font-weight: 400 !important;
  color: #494E53 !important;
}

::v-deep .select__value--placeholder {
  color: #8E9DB7 !important;
}

::v-deep .select__dropdown {
  background-color: #FFFFFF !important;
  border: none !important;
  border-radius: 16px !important;
  padding: 4px !important;
  box-shadow: 0px 24px 30px 0px #00000021 !important;
}

::v-deep .select__option {
  font-family: 'Inter', sans-serif !important;
  font-size: 22px !important;
  font-weight: 400 !important;
  color: #313437 !important;
  padding: 16px 24px !important;
  border-radius: 16px !important;
}

::v-deep .select__option:hover {
  background-color: #EBF2FF !important;
  border-radius: 16px !important;
}
</style>
